import {FC, useState, useEffect, useCallback, useMemo} from 'react'
import { getTableConfig } from './utils/persistConfig'
import { ColumnSelector } from './components/ColumnSelector'
import {ModularDataTableProps, TablePagination, TableSorting, TableSelection} from './types'
import {TableHeader} from './components/TableHeader'
import {TableBody} from './components/TableBody'
import {TableFooter} from './components/TableFooter'
import {TableSearch} from './components/TableSearch'
import {TableLoading, TableError} from './components/TableStates'
import {searchInData, sortData, paginateData} from './utils'

interface ModularDataTableWithPersistProps extends ModularDataTableProps {
  persistPageKey?: string
  persistContextKey?: string
}

export const ModularDataTable: FC<ModularDataTableWithPersistProps> = ({
  // Dados
  data = [],
  columns,

  // Paginação
  pagination: externalPagination,
  onPaginationChange,

  // Ordenação
  sorting: externalSorting,
  onSortingChange,

  // Filtros e busca
  searchable = true,
  searchPlaceholder = 'Pesquisar...',
  onFilterChange,

  // Seleção
  selectable = false,
  selection: externalSelection,
  onSelectionChange,

  // Ações
  actions,

  // Configurações visuais
  striped = true,
  bordered = false,
  hover = true,
  size = 'md',
  className = '',
  alterColumns = false,

  // Estados
  loading = false,
  error,

  // Callbacks
  onDataChange,
}) => {
  // Debug: log do loading
  console.log('ModularDataTable loading:', loading)
  // Persistência contextualizada
  const persistPageKey = typeof window !== 'undefined' && (window as any).location?.pathname || 'default_table'
  const persistContextKey = undefined
  
  // Final para uso
  const persistKey = persistPageKey || (typeof window !== 'undefined' && window.location?.pathname) || 'default_table'
  const contextKey = persistContextKey

  // Carrega config persistida
  const persisted = getTableConfig(persistKey, contextKey) || {}
  const [internalPagination, setInternalPagination] = useState<TablePagination>({
    page: 1,
    pageSize: 25,
    total: 0,
    pageSizes: [10, 25, 50, 100],
  })

  const [internalSorting, setInternalSorting] = useState<TableSorting>({
    field: '',
    direction: 'asc',
  })

  const [internalSelection, setInternalSelection] = useState<TableSelection>({
    selectedRows: [],
    selectAll: false,
  })

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('')

  // Estado para seleção dinâmica de colunas
  const [showColumnSelector, setShowColumnSelector] = useState(false)
  const [dynamicColumns, setDynamicColumns] = useState(() => columns.map(col => ({ ...col })))

  // Sincroniza columns prop com estado local se alterar
  useEffect(() => {
    setDynamicColumns(columns.map(col => ({ ...col })))
  }, [columns])

  // Determina se está usando estado interno ou externo
  const pagination = externalPagination || internalPagination
  const sorting = externalSorting || internalSorting
  const selection = externalSelection || internalSelection

  // Debounce da busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Processa os dados baseado nos filtros, ordenação e busca
  const processedData = useMemo(() => {
    let result = [...data]

    // Aplica busca
    if (debouncedSearchTerm && !onDataChange) {
      result = searchInData(result, debouncedSearchTerm)
    }

    // Aplica ordenação (apenas se não houver callback externo)
    if (sorting.field && !onDataChange) {
      result = sortData(result, sorting.field, sorting.direction)
    }

    return result
  }, [data, debouncedSearchTerm, sorting, onDataChange])

  // Dados paginados (apenas se não houver callback externo)
  const paginatedData = useMemo(() => {
    if (onDataChange) {
      return data // Se há callback externo, assume que os dados já vêm paginados
    }

    return paginateData(processedData, pagination.page, pagination.pageSize)
  }, [processedData, pagination.page, pagination.pageSize, onDataChange, data])

  // Atualiza total quando dados mudam
  useEffect(() => {
    if (!externalPagination) {
      setInternalPagination((prev) => ({
        ...prev,
        total: processedData.length,
      }))
    }
  }, [processedData.length, externalPagination])

  // Callback para mudança de paginação
  const handlePaginationChange = useCallback(
    (newPagination: TablePagination) => {
      if (onPaginationChange) {
        onPaginationChange(newPagination)
      } else {
        setInternalPagination(newPagination)
      }

      // Chama callback de mudança de dados se existir
      if (onDataChange) {
        onDataChange({
          page: newPagination.page,
          pageSize: newPagination.pageSize,
          search: debouncedSearchTerm,
          sorting: sorting.field ? sorting : undefined,
        })
      }
    },
    [onPaginationChange, onDataChange, debouncedSearchTerm, sorting]
  )

  // Callback para mudança de ordenação
  const handleSortingChange = useCallback(
    (newSorting: TableSorting) => {
      if (onSortingChange) {
        onSortingChange(newSorting)
      } else {
        setInternalSorting(newSorting)
      }

      // Chama callback de mudança de dados se existir
      if (onDataChange) {
        onDataChange({
          page: pagination.page,
          pageSize: pagination.pageSize,
          search: debouncedSearchTerm,
          sorting: newSorting,
        })
      }
    },
    [onSortingChange, onDataChange, pagination.page, pagination.pageSize, debouncedSearchTerm]
  )

  // Callback para mudança de seleção
  const handleSelectionChange = useCallback(
    (newSelection: TableSelection) => {
      if (onSelectionChange) {
        onSelectionChange(newSelection)
      } else {
        setInternalSelection(newSelection)
      }
    },
    [onSelectionChange]
  )

  useEffect(() => {
    if (onDataChange && debouncedSearchTerm !== searchTerm) {
      onDataChange({
        page: 1, // Volta para primeira página ao pesquisar
        pageSize: pagination.pageSize,
        search: searchTerm,
        sorting: sorting.field ? sorting : undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm])

  const tableClasses = [
    'table',
    striped ? 'table-striped' : '',
    bordered ? 'table-bordered' : '',
    hover ? 'table-hover' : '',
    `table-${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const visibleColumns = dynamicColumns.filter((col) => col.visible !== false)
  const hasActions = actions && actions.length > 0

  return (
    <div className='card' style={{ position: 'relative' }}>
      {/* Header da tabela com busca e controles */}
      <div className='card-header border-0 pt-5 d-flex align-items-center justify-content-between'>
        <div style={{ flex: 1 }}>
          <TableSearch
            search={searchTerm}
            onSearchChange={setSearchTerm}
            onSearchSubmit={() => {
              if (onDataChange) {
                onDataChange({
                  page: 1,
                  pageSize: pagination.pageSize,
                  search: searchTerm,
                  sorting: sorting.field ? sorting : undefined,
                })
              }
            }}
            clearSearch={() => {
              if (onDataChange) {
                onDataChange({
                  page: 1,
                  pageSize: pagination.pageSize,
                  search: '',
                  sorting: sorting.field ? sorting : undefined,
                })
              }
            }}
            pageSize={pagination.pageSize}
            onPageSizeChange={(newPageSize) => {
              handlePaginationChange({
                ...pagination,
                pageSize: newPageSize,
                page: 1,
              })
            }}
            searchPlaceholder={searchPlaceholder}
            disabled={loading}
          />
        </div>
        {alterColumns && (
          <button
            className='btn btn-light btn-sm ms-2'
            title='Selecionar colunas'
            style={{ minWidth: 36, minHeight: 36, borderRadius: '50%' }}
            onClick={() => setShowColumnSelector((v) => !v)}
          >
            <span className='bi bi-gear' style={{ fontSize: 18 }} />
            {/* Se não usar Bootstrap Icons, pode usar: */}
            {/* <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/><path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115l.094-.319z"/></svg> */}
          </button>
        )}
        {showColumnSelector && (
          <ColumnSelector
            columns={dynamicColumns}
            onChange={(key, visible) => {
              setDynamicColumns(cols => cols.map(col => col.key === key ? { ...col, visible } : col))
            }}
            onClose={() => setShowColumnSelector(false)}
          />
        )}
      </div>

      {/* Corpo da tabela */}
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className={tableClasses}>
            <TableHeader
              columns={visibleColumns}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              selectable={selectable}
              selection={selection}
              onSelectionChange={handleSelectionChange}
              totalRows={paginatedData.length}
            />

            {loading ? (
              <TableLoading
                columns={visibleColumns.length}
                selectable={selectable}
                hasActions={hasActions}
              />
            ) : error ? (
              <TableError
                message={error}
                onRetry={() =>
                  onDataChange &&
                  onDataChange({
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                    search: debouncedSearchTerm,
                    sorting: sorting.field ? sorting : undefined,
                  })
                }
                columns={visibleColumns.length}
                selectable={selectable}
                hasActions={hasActions}
              />
            ) : (
              <TableBody
                data={paginatedData}
                columns={visibleColumns}
                actions={actions}
                selectable={selectable}
                selection={selection}
                onSelectionChange={handleSelectionChange}
                striped={striped}
                hover={hover}
              />
            )}
          </table>
        </div>
      </div>

      {/* Footer da tabela com paginação */}
      {pagination.total > 0 && (
        <div className='card-footer py-4'>
          <TableFooter
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            loading={loading}
            color='primary'
          />
        </div>
      )}
    </div>
  )
}

export default ModularDataTable
